import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import isSameDay from 'date-fns/isSameDay'
import isBefore from 'date-fns/isBefore'
import { StockType, StockUnionType } from 'types/stock'
import { DefaultStockType, ScheduleType } from 'types/schedule'
import { Spacer } from 'components/Utility'
import { Caution, SmallHeadline, SubDescription } from 'components/Typography'
import { dateToString, parseTimeToDate, parseToDate, sumCheckoutItemsAmount } from 'lib/helper'
import { useStringChangeEvent } from 'hooks/form'
import { useHandleDefaultStock } from 'hooks/stock'
import { HoursToDeadlineForm, IndividualStockForm, Textarea } from 'components/Form'
import { NavButton, OrangeButton, TextButton } from 'components/Button'
import { addMinutes, differenceInMinutes } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { getOpenTime, getStoreName } from 'reducks/store/selectors'
import { State } from 'index'
import { BreadType } from 'types/bread'
import { getStoreRef } from 'reducks/auth/selectors'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { FlexColumn } from 'components/Flex'
import { OpenTimeType } from 'types/store'
import { getLatestOpenTime, getOpenTimeOnDay } from 'lib/businessHour'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import getInitialStock from 'reducks/stock/operations/getInitialStock'
import { useHandleIndividualHoursToDeadline } from 'hooks/stock'
import { getOrders } from 'reducks/checkout/selectors'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'

type Props = {
  backToSelectDate?: () => void
  bread: BreadType
  date: Date
  isFixedDate?: boolean
  saveIndividualSettings: (
    stocks: StockType[],
    defaultStocks: DefaultStockType[],
    hoursToDeadline: number,
    note: string,
    defaultSchedule: ScheduleType
  ) => void
  schedules: ScheduleType[]
  stocks: StockType[]
  type: StockUnionType
}

const getPrevHoursToDeadline = (schedule: ScheduleType | undefined, stocks: StockType[]): number => {
  if (stocks.length === 0) {
    if (!schedule) return 0
    return schedule.hours_to_deadline
  } else {
    const stock = stocks[0]
    if (!stock?.deadline) return 0
    return differenceInMinutes(parseToDate(stock.date), stock.deadline.toDate()) / 60
  }
}

const IndividualScheduleEditCard: FC<Props> = (props) => {
  const { backToSelectDate, bread, date, isFixedDate, saveIndividualSettings, schedules, stocks, type } = props

  const dispatch = useDispatch()
  const daysOfWeek = new DaysOfWeek()
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const businessHours: OpenTimeType = useSelector((state: State) => getOpenTime(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeName = useSelector((state: State) => getStoreName(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const orders = useSelector((state: State) => getOrders(state))

  const dayOfWeek = daysOfWeek.getDayOfWeek(date, specialHolidays)
  const temporaryOpen = useGetTemporaryOpen(dateToString(date), storeCalendars)
  const defaultDayOfWeek = temporaryOpen?.default_day_of_week || dayOfWeek
  const schedule = schedules.find((schedule) => schedule.days_of_week.includes(defaultDayOfWeek))
  const prevHoursToDeadline = getPrevHoursToDeadline(schedule, stocks)
  const openTime = getOpenTimeOnDay(dayOfWeek, businessHours, dateToString(date), storeCalendars)

  const [defaultStocks, setDefaultStocks] = useState<DefaultStockType[]>([{ pickupable_time: '', stock: 0 }])
  const [note, setNote] = useState<string>('')
  const [stockList, setStockList] = useState<StockType[]>([])
  const [error, setError] = useState<string>('')

  const handleNote = useStringChangeEvent(setNote)
  const handleDefaultStocks = useHandleDefaultStock(setDefaultStocks)
  const { hoursToDeadline, handleHoursToDeadline } = useHandleIndividualHoursToDeadline(prevHoursToDeadline)

  // 現在の注文数と有効在庫数を計算する
  const checkoutItems = useCheckoutItemsFromOrders(orders, bread.bread_ref)
  const checkoutItemsAmount = sumCheckoutItemsAmount(checkoutItems)
  const stocksAmount = defaultStocks.reduce((prev, defaultStock) => prev + defaultStock.stock, 0)
  const availableStocksAmount = stocksAmount - checkoutItemsAmount

  const addDefaultStock = useCallback(() => {
    const defaultSchedule = schedule ?? schedules[0]
    if (!defaultSchedule) return
    const latestOpenTime = getLatestOpenTime(defaultSchedule.days_of_week, businessHours)
    const initialStock = getInitialStock(
      bread,
      dateToString(date),
      latestOpenTime,
      defaultSchedule,
      0,
      undefined,
      storeName,
      storeRef,
      type
    )
    // sacri stationの場合は生産店舗のrefを追加
    if (defaultSchedule.manufacturer_ref) {
      initialStock.manufacturer_ref = defaultSchedule.manufacturer_ref
    }
    setStockList((prevState) => [...prevState, initialStock])
    setDefaultStocks((prevState) => [...prevState, { pickupable_time: latestOpenTime, stock: 0 }])
  }, [setDefaultStocks, setStockList, schedule, schedules, bread, storeName, storeRef, businessHours, type])

  const deleteDefaultStock = useCallback(
    (index: number) => {
      setStockList((prevState) => {
        const nextState = prevState.filter((_, i) => i !== index)
        return [...nextState]
      })
      setDefaultStocks((prevState) => {
        const nextState = prevState.filter((_, i) => i !== index)
        return [...nextState]
      })
    },
    [setDefaultStocks, setStockList]
  )

  // 初期値の設定
  useEffect(() => {
    // 在庫が未設定
    if (stocks.length === 0) {
      const initialStocks: StockType[] = []
      if (!schedule) {
        const defaultSchedule = schedules[0]
        if (!defaultSchedule) return
        const latestOpenTime = getLatestOpenTime(defaultSchedule.days_of_week, businessHours)
        const initialStock = getInitialStock(
          bread,
          dateToString(date),
          latestOpenTime,
          defaultSchedule,
          0,
          undefined,
          storeName,
          storeRef,
          type
        )
        initialStocks.push(initialStock)
      } else {
        for (let i = 0; i < schedule.default_stocks.length; i = (i + 1) | 0) {
          const defaultStock = schedule.default_stocks[i]
          const stockRef = storeRef
            .collection('store_stocks')
            .doc(`stock_${type}_${schedule.schedule_ref?.id}_${dateToString(date)}_${defaultStock.pickupable_time}`)
          const initialStock = getInitialStock(
            bread,
            dateToString(date),
            defaultStock.pickupable_time,
            schedule,
            defaultStock.stock,
            stockRef,
            storeName,
            storeRef,
            type
          )
          initialStocks.push(initialStock)
        }
        setDefaultStocks([...schedule.default_stocks])
        setNote(schedule.note)
      }
      setStockList([...initialStocks])
    } else {
      const stock = stocks[0]
      if (!stock.deadline) return
      setNote(stock.note)
      const _defaultStocks: DefaultStockType[] = []
      stocks.map((_stock) => _defaultStocks.push({ pickupable_time: _stock.pickupable_time, stock: _stock.stock }))
      setDefaultStocks([..._defaultStocks])
      setStockList([...stocks])
    }
  }, [stocks, bread, storeRef, storeName, schedule, schedules, businessHours, specialHolidays, type])

  const cannotAddDefaultStock = useMemo(() => {
    return defaultStocks.some((defaultStock) => defaultStock.pickupable_time === '')
  }, [defaultStocks])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initOrders())
    dispatch(fetchOrders(dateToString(date), 'すべて', ['preorder'], ['requires_capture', 'succeeded'], storeRef))
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [storeRef, date])

  return (
    <div>
      <SmallHeadline label={'注文締め切り'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'お客さまが、アプリ上で購入できる締切日です。'} />
      <Spacer size={8} />
      <HoursToDeadlineForm
        daysOfWeek={[daysOfWeek.getDayOfWeek(date, specialHolidays)]}
        handleHoursToDeadline={handleHoursToDeadline}
        prevHoursToDeadline={prevHoursToDeadline}
        temporaryOpen={temporaryOpen}
      />
      <Spacer size={16} />
      <SmallHeadline label={'1日の販売上限数'} required={true} />
      <Spacer size={8} />
      <SubDescription
        description={`１日に販売できる上限を入力してください。\n※${checkoutItemsAmount}個の注文が入っており、残り在庫は${availableStocksAmount}個です`}
      />
      <Spacer size={8} />
      <IndividualStockForm
        date={date}
        daysOfWeek={[daysOfWeek.getDayOfWeek(date, specialHolidays)]}
        defaultStocks={defaultStocks}
        deleteDefaultStock={deleteDefaultStock}
        handleDefaultStocks={handleDefaultStocks}
        type={type}
      />
      <Spacer size={16} />
      <FlexColumn alignItems={'center'}>
        <TextButton
          disabled={cannotAddDefaultStock}
          iconPath={cannotAddDefaultStock ? '/icon/grey/plus.svg' : '/icon/blue/plus.svg'}
          label={'受渡解禁時刻を追加する'}
          onClick={addDefaultStock}
        />
      </FlexColumn>
      <Spacer size={16} />
      <SmallHeadline label={'商品受渡し時の注意点'} />
      <Spacer size={8} />
      <Textarea disabled={false} onChange={handleNote} maxLength={280} required={false} rows={10} value={note} />
      {closedDates.some((closedDate) => isSameDay(closedDate, date)) && (
        <>
          <Spacer size={8} />
          <Caution caution={'この日はお店が休業日に設定されていますがよろしいですか？'} />
        </>
      )}
      {error !== '' && (
        <>
          <Spacer size={8} />
          <Caution caution={error} />
        </>
      )}
      <Spacer size={16} />
      <OrangeButton
        label={'保存する'}
        onClick={() => {
          const deadline = addMinutes(date, -(hoursToDeadline * 60))
          if (isBefore(deadline, new Date())) {
            setError('注文締め切りが過ぎています。正しい日付に修正してください。')
          } else if (stocks.length === 0 && defaultStocks.some((defaultStock) => defaultStock.stock === 0)) {
            setError('販売上限数は1以上に設定してください。')
          } else if (
            defaultStocks.some(
              (defaultStock) =>
                openTime && isBefore(parseTimeToDate(defaultStock.pickupable_time), parseTimeToDate(openTime))
            )
          ) {
            setError('受渡解禁時刻は店舗オープン時刻より遅い時間に設定してください。')
          } else {
            setError('')
            saveIndividualSettings(stockList, defaultStocks, hoursToDeadline, note, schedule ?? schedules[0])
          }
        }}
        size={'medium'}
        type={'main'}
      />
      {/* 販売予定一覧からの導線では日付選択させない */}
      {!isFixedDate && (
        <>
          <Spacer size={12} />
          <NavButton
            label={'日付の選択に戻る'}
            iconPath={'/icon/grey/arrow_prev.svg'}
            onClick={backToSelectDate}
            url={''}
          />
        </>
      )}
    </div>
  )
}

export default IndividualScheduleEditCard
